import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrencyXCD } from '../config/JadConfig';
import { ChefHeader, SegmentedControl } from '../components/ios';
import { ChevronRight } from 'lucide-react';
import { normalizeStatus, COMPLETED_STATUSES } from '../utils/orderStatus';

// Mirrors KFCStKitts OrderHistoryView: ChefHeader("Orders") + Active/Completed
// segmented picker + rounded order rows + "No more orders to show" footer.
// Statuses may be stored 'Pending', 'In Delivery', 'inDelivery', 'picked_up'…
// — normalize before comparing so live orders keep classifying correctly.
// (normalizeStatus/COMPLETED_STATUSES shared via utils/orderStatus.)

// iOS OrderHistoryRow statusColor/formattedStatus — system colors.
const STATUS_STYLES = {
  pending:    { color: '#FF9500', label: 'Order Placed' },      // .orange
  confirmed:  { color: '#007AFF', label: 'Confirmed' },         // .blue
  preparing:  { color: '#FFCC00', label: 'Preparing' },         // .yellow
  ready:      { color: '#34C759', label: 'Ready for Pickup' },  // .green
  onmyway:    { color: '#AF52DE', label: 'On the Way' },        // .purple
  indelivery: { color: '#AF52DE', label: 'In Delivery' },       // .purple
  arrived:    { color: '#5856D6', label: 'Arrived' },           // .indigo
  delivered:  { color: '#34C759', label: 'Delivered' },         // .green
  completed:  { color: '#34C759', label: 'Completed' },         // .green
  cancelled:  { color: '#FF3B30', label: 'Cancelled' },         // .red
  pickedup:   { color: '#34C759', label: 'Picked Up' },         // .green
};

const getStatusInfo = (status) =>
  STATUS_STYLES[normalizeStatus(status)] || { color: '#8E8E93', label: status || 'Unknown' };

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const OrdersPage = () => {
  const { currentUser } = useAuth();
  const { db } = useFirebase();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Active'); // iOS selectedTab: 0 = Active, 1 = Completed

  useEffect(() => {
    if (currentUser) {
      setLoading(true);
      let unsubscribe = null;

      try {
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('customerId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );

        unsubscribe = onSnapshot(q, (snapshot) => {
          const ordersList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date()
          }));
          setOrders(ordersList);
          setError(null);
          setLoading(false);
        }, (err) => {
          console.error('Error fetching orders:', err);
          setError('Failed to load orders. Please try again later.');
          setLoading(false);
        });
      } catch (err) {
        console.error('Error setting up order listener:', err);
        setError('Failed to load orders. Please try again later.');
        setLoading(false);
      }

      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    } else {
      setLoading(false);
      setError('Please sign in to view your orders');
    }
  }, [currentUser, db]);

  const userOrders = currentUser ? orders.filter(order => order.customerId === currentUser.uid) : [];

  // iOS: isCompleted → Completed tab, everything else → Active tab.
  const activeOrders = userOrders.filter(order => !COMPLETED_STATUSES.includes(normalizeStatus(order.status)));
  const completedOrders = userOrders.filter(order => COMPLETED_STATUSES.includes(normalizeStatus(order.status)));

  const currentOrders = activeTab === 'Active' ? activeOrders : completedOrders;

  return (
    <div className="bg-[#F2F2F7] min-h-full">
      <ChefHeader title="Orders" />

      <div className="pt-2">
        <SegmentedControl
          options={[
            { label: 'Active', value: 'Active', count: activeOrders.length, badgeColor: '#FF3B30' },
            { label: 'Completed', value: 'Completed', count: completedOrders.length, badgeColor: '#34C759' },
          ]}
          value={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div className="px-4 py-3 max-w-lg mx-auto">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-[15px] text-[#FF3B30]">{error}</p>
          </div>
        ) : currentOrders.length === 0 ? (
          // iOS: centered secondary headline "No active/completed orders"
          <p className="text-center text-[15px] font-semibold text-[#8E8E93] py-20">
            {activeTab === 'Active' ? 'No active orders' : 'No completed orders'}
          </p>
        ) : (
          <div className="space-y-2">
            {currentOrders.map((order) => (
              <OrderHistoryRow
                key={order.id}
                order={order}
                onClick={() => navigate(`/orders/${order.id}`)}
              />
            ))}

            <p className="text-center text-[#8E8E93] text-xs py-2">
              No more orders to show
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Mirrors iOS OrderHistoryRow: order number + status capsule on top line,
// restaurant + item count below; total + date trailing; chevron pushes detail.
function OrderHistoryRow({ order, onClick }) {
  const info = getStatusInfo(order.status);
  const orderItems = order.items || order.orderItems || [];
  const itemCount = orderItems.reduce((sum, it) => sum + (it.quantity || 1), 0);

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:bg-gray-50 transition-colors"
    >
      <div className="flex gap-3">
        {/* Leading column */}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              Order #{order.id.slice(0, 8)}
            </span>
            <span
              className="px-1.5 py-0.5 rounded-full text-[11px] leading-tight whitespace-nowrap shrink-0"
              style={{ backgroundColor: `${info.color}26`, color: info.color }}
            >
              {info.label}
            </span>
          </div>

          <p className="text-xs text-[#8E8E93] truncate">
            {order.restaurantName || order.restaurantLocation?.name || 'Restaurant'}
          </p>

          {itemCount > 0 && (
            <p className="text-[11px] text-[#8E8E93]">
              {itemCount} item{itemCount > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Trailing column */}
        <div className="text-right shrink-0 space-y-0.5">
          <p className="text-sm font-semibold text-gray-900">
            {formatCurrencyXCD(order.totalAmount || 0)}
          </p>
          <p className="text-[11px] text-[#8E8E93]">
            {formatDate(order.createdAt)}
          </p>
        </div>

        <ChevronRight size={17} className="text-[#C7C7CC] self-center shrink-0" />
      </div>
    </button>
  );
}

export default OrdersPage;
